import { Inject, Injectable, InjectionToken } from "@angular/core";
import { map, Observable } from "rxjs";
import { HttpService } from "./http.service";
import { environment } from "@environments/environment";
import { HttpHeaders } from "@angular/common/http";
import { SessionService } from "./session.service";
import { PaymentMethod } from "@models/payment";
import { CreateSubscriptionDto } from "../request-dtos/subscription.dto";

/**
 * API `POST .../payments/create-payment-intent` — Stripe (client_secret) or Polar (redirect).
 */
export interface CreateWalletPaymentResponse {
    paymentProvider: "stripe" | "polar";
    flow: "client_secret" | "redirect";
    amount: number;
    id?: string;
    client_secret?: string;
    url?: string;
    checkoutId?: string;
    currency?: string;
}

export const PLUTO_ID = new InjectionToken<string>('[PLUTO] ClientID');

export interface BillingHistoryOverviewRecord {
    id: number;
    provider: string;
    status: 'paid' | 'failed' | 'canceled' | 'refunded' | string;
    amount: number | null;
    currency: string | null;
    description: string | null;
    rawEventType: string;
    occurredAt: string | Date | null;
    providerPaymentId: string | null;
    providerInvoiceId: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService {


    constructor(private http: HttpService,
                @Inject(PLUTO_ID) private readonly clientId: string,
                private sessionSvc:SessionService
    ) {}


    createPaymentIntent(
        params: { amount: number; email: string; paymentMethodId?: string; userId?: number; workspaceId?: number },
        userMode: boolean = false,
    ): Observable<CreateWalletPaymentResponse> {
        const url = userMode
            ? `${environment.apiUrl}/user/payments/create-payment-intent`
            : `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/create-payment-intent`;
        return this.http
            .post<{ data: CreateWalletPaymentResponse }>(url, params)
            .pipe(map((res) => res.data));
    }


    subscribeToPlan(createSubscriptionDto: CreateSubscriptionDto) {
        return this.http.post(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/create-subscription`, createSubscriptionDto).pipe(map((data: any)=>{
            let subscription: any = data.data;
            return subscription;
        }));

    }


    addPaymentMethod(paymentMethodId: string, userMode: boolean = false) {
      let url = `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/create-payment-method`;
      if(userMode){
        url = `${environment.apiUrl}/user/add-payment-method`;
      }
      return this.http.post(
        url,
        { paymentMethodId }
      ).pipe(map((data: any) => {
        let paymentMethods: PaymentMethod[] = data.data;
        return paymentMethods;
      }));
    }

    getPaymentMethods(userMode: boolean = false): Observable<PaymentMethod[]> {
      let url = `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/`;
      if(userMode){
        url = `${environment.apiUrl}/user/payment-methods`;
      }
        return this.http.get(
          url
        ).pipe(map((data: any) => {
            let paymentMethods: PaymentMethod[] = data.data;
            return paymentMethods;
        }));
    }

    removePaymentMethod(paymentMethodId: number, userMode: boolean = false): Observable<PaymentMethod> {
      let url = `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/${paymentMethodId}`;
      if(userMode){
        url = `${environment.apiUrl}/user/payment-methods/${paymentMethodId}`;
      }
        return this.http.delete(url).pipe(map((data: any)=>{
            let paymentMethod: PaymentMethod = data.data;
            return paymentMethod;
        }));
    }


    cancelSubscription(): Observable<any> {
      return this.http.get(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/cancel-subscription`).pipe(map((data: any)=>{
          let success: any = data.data;
          return success;
      }));
  }

    setDefaultPaymentMethod(paymentMethodId: number): Observable<any> {
        return this.http.patch(
            `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/set-default-payment-method?paymentMethodId=${paymentMethodId}`,
            {}
        ).pipe(map((data: any) => {
            return data.data;
        }));
    }

    /** Sync Stripe cancel_at_period_end with DB autoRenew (workspace vs user billing). */
    setSubscriptionAutoRenew(
        enabled: boolean,
        userMode: boolean,
    ): Observable<{
        autoRenew: boolean;
        startDate?: string;
        endDate?: string;
        status?: string;
    }> {
        const body = { enabled };
        const url = userMode
            ? `${environment.apiUrl}/user/payments/subscription/auto-renew`
            : `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/subscription/auto-renew`;
        return this.http.patch(url, body).pipe(
            map((data: any) => data.data),
        );
    }

    /**
     * Get Stripe checkout URL for upgrading to agency plan
     * @returns Observable<string> - The Stripe checkout URL
     */
    getAgencyUpgradeUrl(): Observable<string> {
        return this.http.post(
            `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/manage-subscription/create-checkout-session`,
            {}
        ).pipe(map((data: any) => {
            return data.data.url;
        }));
    }

    /**
     * Create Stripe checkout session for a workspace catalog package.
     * Uses `/:workspaceUid/payments/...` so WorkspaceGuard sets workspace on the API session.
     */
    createSubscriptionCheckoutSession(packageId: number): Observable<string> {
        const ws = this.sessionSvc.getUserWorkspace();
        return this.http.post(
            `${environment.apiUrl}/${ws.uid}/payments/manage-subscription/create-checkout-session/${packageId}`,
            {},
        ).pipe(map((data: any) => {
            return data.data.url;
        }));
    }

    /**
     * Polar: in-place plan change, or redirect to Checkout when not subscribed yet.
     * @see shouldUseCheckoutForPlanChange — call createSubscriptionCheckoutSession when that is true.
     */
    changeSubscriptionPlan(
        packageId: number,
    ): Observable<
        | { kind: "redirect"; url: string }
        | { kind: "subscription_updated" }
    > {
        const ws = this.sessionSvc.getUserWorkspace();
        return this.http
            .post(
                `${environment.apiUrl}/${ws.uid}/payments/manage-subscription/change-plan/${packageId}`,
                {},
            )
            .pipe(
                map((res: { data: { flow?: string; url?: string } }) => {
                    const d = res.data;
                    if (d?.flow === "subscription_updated") {
                        return { kind: "subscription_updated" as const };
                    }
                    if (d?.url && typeof d.url === "string") {
                        return { kind: "redirect" as const, url: d.url };
                    }
                    throw new Error("Unexpected plan change response");
                }),
            );
    }

    getBillingHistoryOverview(
        userMode: boolean = false,
        limit = 10,
    ): Observable<BillingHistoryOverviewRecord[]> {
        const url = userMode
            ? `${environment.apiUrl}/user/billing-history/overview?limit=${limit}`
            : `${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/billing-history/overview?limit=${limit}`;

        return this.http.get(url).pipe(map((data: any) => data.data));
    }

    /** Server default gateway for new users (first paid touch), read-only. */
    getBillingBootstrap(): Observable<{ defaultPaymentProvider: string }> {
        return this.http
            .get(`${environment.apiUrl}/${this.sessionSvc.getUserWorkspace().uid}/payments/billing-bootstrap`)
            .pipe(map((data: { data: { defaultPaymentProvider: string } }) => data.data));
    }
}